import {
  ClipboardCheck,
  FileSearch,
  KanbanSquare,
  MonitorSmartphone,
  Package,
  Smartphone,
} from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";

const SERVICES = [
  {
    icon: FileSearch,
    title: "Business Analysis",
    description:
      "Transform business problems and ideas into clear, structured requirements that teams can understand and execute.",
    items: [
      "Business Requirements",
      "BRD",
      "Functional Requirements",
      "Process Analysis",
      "Workflow Mapping",
    ],
  },
  {
    icon: Package,
    title: "Product Definition",
    description:
      "Define what your digital product needs to do before development begins.",
    items: [
      "PRD",
      "Product Scope",
      "User Journeys",
      "Feature Definition",
      "Product Requirements",
    ],
  },
  {
    icon: KanbanSquare,
    title: "User Stories & Agile Delivery",
    description:
      "Convert requirements into development-ready Agile artifacts.",
    items: [
      "User Stories",
      "Acceptance Criteria",
      "Product Backlog",
      "Prioritization",
      "Sprint-ready Requirements",
    ],
  },
  {
    icon: ClipboardCheck,
    title: "UAT & Quality Readiness",
    description:
      "Ensure the delivered product matches business requirements before release.",
    items: [
      "UAT Scenarios",
      "Test Cases",
      "Acceptance Criteria",
      "Requirement Traceability",
      "Defect Validation",
    ],
  },
  {
    icon: MonitorSmartphone,
    title: "Websites & Web Applications",
    description:
      "Design and build responsive digital experiences for businesses, startups and product ideas.",
    items: [
      "Business Websites",
      "Responsive Web Applications",
      "SaaS Interfaces",
      "Booking Platforms",
      "Custom Business Portals",
    ],
  },
  {
    icon: Smartphone,
    title: "Mobile App Solutions",
    description:
      "Turn validated product requirements into mobile experiences for Android and iOS.",
    items: [
      "Android Applications",
      "iOS Applications",
      "Cross-platform Applications",
      "Backend Integration",
      "App Product Definition",
    ],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="scroll-mt-24 border-y border-navy/10 bg-white py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal>
          <Eyebrow>Services</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-4xl font-semibold leading-[1.15] tracking-tight md:text-5xl">
            What I can help you build
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((service, i) => (
            <Reveal key={service.title} delay={(i % 3) * 0.08}>
              <article
                data-testid={`service-card-${i}`}
                className="group flex h-full flex-col border border-navy/15 bg-white p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-navy/40"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-navy/15 bg-cream text-navy transition-colors duration-300 group-hover:border-rust group-hover:bg-rust group-hover:text-white">
                    <service.icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <span className="font-display text-sm text-navy/30">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-7 font-display text-2xl font-medium leading-snug tracking-tight">
                  {service.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-navy/70">
                  {service.description}
                </p>
                <ul className="mt-6 space-y-2.5 border-t border-navy/10 pt-6">
                  {service.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-3 text-sm text-navy/75"
                    >
                      <span className="h-1 w-1 shrink-0 rounded-full bg-rust" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-14">
            <a
              href="#contact"
              data-testid="services-discuss-requirements-btn"
              className="inline-flex items-center rounded-full bg-navy px-8 py-3.5 text-sm font-medium text-cream transition-transform duration-300 hover:-translate-y-0.5"
            >
              Discuss Your Requirements
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
